/**
 * Global Feature Flag Manager
 * Manages a single PostHog listener for all feature flag subscriptions
 * Prevents memory leaks by centralizing callback management
 */

import { PostHog } from 'posthog-js';
import PosthogService from './PosthogService';

type FlagListener = {
  flagKey: string;
  callback: (enabled: boolean, payload?: any) => void;
};

class FeatureFlagManager {
  private static instance: FeatureFlagManager;
  private listeners: Map<symbol, FlagListener> = new Map();
  private posthogInstance: PostHog | null = null;
  private isListening: boolean = false;

  private constructor() {}

  static getInstance(): FeatureFlagManager {
    if (!FeatureFlagManager.instance) {
      FeatureFlagManager.instance = new FeatureFlagManager();
    }
    return FeatureFlagManager.instance;
  }

  /**
   * Initialize the manager with PostHog instance
   */
  initialize(posthogService: PosthogService): void {
    const posthog = posthogService.getPosthogInstance();

    if (!posthog || this.posthogInstance === posthog) {
      return; // No instance available or already initialized with this instance
    }

    this.posthogInstance = posthog;

    // Also ensure we're using the same instance as the toolbar
    if ((window as any).posthog && (window as any).posthog !== posthog) {
      this.posthogInstance = (window as any).posthog;
    }

    this.setupGlobalListener();
  }

  /**
   * Set up the single global listener for all feature flags
   */
  private setupGlobalListener(): void {
    if (!this.posthogInstance || this.isListening) {
      return;
    }

    // Single callback that handles all feature flag updates
    this.posthogInstance.onFeatureFlags(() => {
      // Check ALL listeners, not just the changed ones
      this.listeners.forEach((listener) => {
        const enabled = this.posthogInstance!.isFeatureEnabled(listener.flagKey) ?? false;
        const payload = this.posthogInstance!.getFeatureFlagPayload(listener.flagKey);
        listener.callback(enabled, payload);
      });
    });

    this.isListening = true;
  }

  /**
   * Subscribe to feature flag changes
   * Returns an unsubscribe function
   */
  subscribe(flagKey: string, callback: (enabled: boolean, payload?: any) => void): () => void {
    const id = Symbol('flag-listener');
    this.listeners.set(id, { flagKey, callback });

    // If this is the first listener and we're not listening yet, set up the global listener
    if (this.listeners.size === 1 && !this.isListening && this.posthogInstance) {
      this.setupGlobalListener();
    }

    // Return unsubscribe function
    return () => {
      this.listeners.delete(id);
    };
  }

  /**
   * Get current flag value immediately
   */
  getFeatureFlag(flagKey: string, defaultValue: boolean = false): boolean {
    if (!this.posthogInstance) {
      return defaultValue;
    }
    return this.posthogInstance.isFeatureEnabled(flagKey) ?? defaultValue;
  }

  /**
   * Get current flag payload immediately
   */
  getFeatureFlagPayload(flagKey: string): any {
    if (!this.posthogInstance) {
      return null;
    }
    return this.posthogInstance.getFeatureFlagPayload(flagKey);
  }

  /**
   * Reload feature flags from server
   */
  reloadFeatureFlags(): void {
    if (this.posthogInstance) {
      this.posthogInstance.reloadFeatureFlags();
    }
  }

  /**
   * Clear all listeners (useful for testing)
   */
  clear(): void {
    this.listeners.clear();
  }
}

export const featureFlagManager = FeatureFlagManager.getInstance();